import { Component, OnInit, ElementRef, ViewChild, forwardRef, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  Validators,
  FormControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { InputForm } from 'ustore-ui';

import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';

export interface CardBrand {
  name: string;
  icon: string;
  length: number[];
}

@Component({
  selector: 'umkt-input-credit-card-number',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputForm, I18nPipe],
  templateUrl: './input-credit-card-number.component.html',
  styleUrls: ['./input-credit-card-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCreditCardNumberComponent),
      multi: true,
    },
  ],
})
export class InputCreditCardNumberComponent
  implements OnInit, ControlValueAccessor
{
  @ViewChild('cardInput', { static: false })
  cardInput!: ElementRef<HTMLInputElement>;
  
  @Output() cardFlagChange = new EventEmitter<string>();
  
  private isDisabled = false;
  cardNumberControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, this.cardNumberValidator.bind(this)],
  });

  private onChange!: (value: string) => void;
  private onTouched!: () => void;

  cardBrands: Record<string, CardBrand> = {
    VISA: { name: 'Visa', icon: 'assets/images/credit-cards/visa.svg', length: [16] },
    MASTERCARD: {
      name: 'Mastercard',
      icon: 'assets/images/credit-cards/mastercard.svg',
      length: [16],
    },
    AMEX: {
      name: 'American Express',
      icon: 'assets/images/credit-cards/amex.svg',
      length: [15],
    },
    UNKNOWN: {
      name: 'Cartão de Crédito',
      icon: 'assets/images/credit-cards/credit-card.svg',
      length: [15, 16],
    },
  };
  private elementRef = inject(ElementRef);
  private i18nService = inject(I18nService);


  ngOnInit() {
    this.setupPasteListener();
    this.isDisabled = this.cardNumberControl.disabled;
    this.cardNumberControl.valueChanges.subscribe(value => {
      if (this.onChange) {
        this.onChange(value);
      }
    });
  }

  private setupPasteListener() {
    this.elementRef.nativeElement.addEventListener(
      'paste',
      (event: ClipboardEvent) => {
        this.handlePaste(event);
      },
      true
    );
  }

  private handlePaste(event: ClipboardEvent) {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';

    const digitsOnly = pastedData.replace(/\D/g, '');

    if (!digitsOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (!input || input.type !== 'text') {
      return;
    }

    const maxDigits = this.getMaxDigitsForValue(digitsOnly);

    if (digitsOnly.length > maxDigits) {
      this.cardNumberControl.setErrors({
        maxLengthExceeded: true,
      });
      this.cardNumberControl.markAsTouched();
      return;
    }

    const formatted = this.formatCardNumber(digitsOnly);

    input.value = formatted;
    this.cardNumberControl.setValue(formatted, { emitEvent: false });

    const cardFlag = this.detectCardBrand(digitsOnly);
    this.cardFlagChange.emit(cardFlag);

    setTimeout(() => {
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);
    });
  }

  private getMaxDigitsForValue(rawDigits: string): number {
    const brandKey = this.detectCardBrand(rawDigits);
    return brandKey === 'AMEX' ? 15 : 16;
  }

  cardNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.replace(/\s/g, '');
    if (!value) return null;

    if (control.errors?.['maxLengthExceeded']) {
      return null;
    }

    if (!/^\d+$/.test(value)) {
      return { invalidCardNumber: true };
    }

    const brandKey = this.detectCardBrand(value);
    const brand = this.cardBrands[brandKey];

    if (!brand.length.includes(value.length)) {
      return { invalidCardNumber: true };
    }

    if (!this.validateLuhn(value)) {
      return { invalidCardNumber: true };
    }

    return null;
  }

  detectCardBrand(cardNumber: string): string {
    const cleanNumber = cardNumber.replace(/\s/g, '');

    if (/^4/.test(cleanNumber)) {
      return 'VISA';
    }

    if (
      /^5[1-5]/.test(cleanNumber) ||
      (cleanNumber.length >= 4 &&
        parseInt(cleanNumber.substring(0, 4)) >= 2221 &&
        parseInt(cleanNumber.substring(0, 4)) <= 2720)
    ) {
      return 'MASTERCARD';
    }

    if (/^3[47]/.test(cleanNumber)) {
      return 'AMEX';
    }

    return 'UNKNOWN';
  }

  validateLuhn(cardNumber: string): boolean {
    let sum = 0;
    let isEven = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber.charAt(i));

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  formatCardNumber(value: string): string {
    const v = value.replace(/\D/g, '');

    const brandKey = this.detectCardBrand(v);
    const maxLength = brandKey === 'AMEX' ? 15 : 16;

    const limited = v.substring(0, maxLength);

    if (brandKey === 'AMEX') {
      if (limited.length <= 4) return limited;
      if (limited.length <= 10)
        return limited.substring(0, 4) + ' ' + limited.substring(4);
      return (
        limited.substring(0, 4) +
        ' ' +
        limited.substring(4, 10) +
        ' ' +
        limited.substring(10, 15)
      );
    } else {
      const parts = [];
      for (let i = 0; i < limited.length; i += 4) {
        parts.push(limited.substring(i, i + 4));
      }
      return parts.join(' ').trim();
    }
  }

  getCardType(cardNumber: string): string {
    if (!cardNumber) return 'UNKNOWN';
    return this.detectCardBrand(cardNumber);
  }

  getCardIcon(cardNumber: string): string {
    const brandKey = this.getCardType(cardNumber);
    return this.cardBrands[brandKey]?.icon || this.cardBrands['UNKNOWN'].icon;
  }

  getCardBrandName(cardNumber: string): string {
    const brandKey = this.getCardType(cardNumber);
    if (brandKey === 'UNKNOWN') {
      return this.i18nService.instant('components.inputCreditCard.unknownBrand');
    }
    return this.cardBrands[brandKey]?.name || '';
  }

  public getCardTypeClass(cardNumber: string): string {
    const brandKey = this.getCardType(cardNumber)
    return brandKey.toLowerCase();
  }

  onNumberKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    if (event.ctrlKey || event.metaKey) {
      if (['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
        return;
      }
    }

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Bloqueia se já atingiu o limite de dígitos
    const input = event.target as HTMLInputElement;
    const digitsOnly = (input.value || '').replace(/\D/g, '');
    
    if (digitsOnly.length >= this.getMaxDigitsForValue(digitsOnly)) {
      event.preventDefault();
    }
  }

  onCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '');

    const formatted = this.formatCardNumber(digitsOnly);

    input.value = formatted;
    this.cardNumberControl.setValue(formatted);

    this.cardFlagChange.emit(this.detectCardBrand(digitsOnly));
  }

  onCardNumberPaste(event: ClipboardEvent): void {
    this.handlePaste(event);
  }

  onCardNumberBlur(): void {
    this.onTouched();
  }

  writeValue(value: string): void {
    this.cardNumberControl.setValue(value || '', { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.cardNumberControl.disable();
    } else {
      this.cardNumberControl.enable();
    }
  }
}
